import React from 'react';
import { useFileUpload } from './hooks/useFileUpload';
import { FileUploadPresentation } from './presentations';

type DefaultFile = NonNullable<
  React.ComponentProps<typeof FileUploadPresentation>['defaultFiles']
>[number];

type BaseProps = {
  /** 'image': 単一画像（選択済みはプレビュー表示）/ 'file': 複数ファイル（選択済みはリスト表示） */
  type: 'image' | 'file';
  /** 受け付ける拡張子（例: ['jpg', 'jpeg', 'png', 'pdf']） */
  accept: string[];
  /** ファイルサイズ上限（MB） */
  maxSizeMb: number;
  /** バリデーションエラーメッセージ（FieldWrapper から渡す想定） */
  error?: string;
  /** アップロード中フラグ。true のとき DropZone を Loading 状態で表示する */
  isUploading?: boolean;
  /** disabled フラグ。true のとき DropZone を非活性状態で表示する */
  disabled?: boolean;
  /**
   * 既存ファイルの削除ボタン押下時のコールバック。type='file' のみ使用。
   * 引数は defaultFiles の index
   */
  onDefaultFileRemove?: (index: number) => void;
};

type SingleProps = BaseProps & {
  /** フォーム値として File オブジェクトを1件のみ保持する（デフォルト） */
  isMultiple?: false;
  value?: File;
  onChange: (file: File | undefined) => void;
  /**
   * 編集画面で既存ファイルを渡す場合（single のみ）。value が undefined のとき初期値として使用する。
   * - DefaultImageFile（url を持つ）: url を preview として使用する
   * - DefaultAttachmentFile（name のみ）: preview なし
   */
  defaultFile?: DefaultFile;
};

type MultipleProps = BaseProps & {
  /**
   * フォーム値として File オブジェクトを複数保持できる（value: File[]）。
   * 1ファイルずつ追加・削除する操作を繰り返して蓄積する想定で、
   * 複数ファイルを同時にアップロードすることを意味しない。
   */
  isMultiple: true;
  value: File[];
  onChange: (files: File[]) => void;
  /**
   * 既存ファイル一覧（URLから取得したもの）。新規追加ファイル（value）とは別にリスト表示される
   */
  defaultFiles?: DefaultFile[];
};

type Props = SingleProps | MultipleProps;

export const FileUpload: React.FC<Props> = ({
  type,
  accept,
  maxSizeMb,
  error,
  isUploading = false,
  disabled = false,
  onDefaultFileRemove,
  ...rest
}) => {
  const hookArgs = rest.isMultiple
    ? { isMultiple: true as const, value: rest.value, onChange: rest.onChange }
    : {
        isMultiple: false as const,
        value: rest.value,
        onChange: rest.onChange,
        defaultFile: rest.defaultFile,
      };

  const {
    state: { internalValue, isDragging, validationError },
    actions: { handleDragOver, handleDragLeave, handleDrop, handleFileInputChange, handleRemove },
    refs: { fileInputRef },
  } = useFileUpload({ type, accept, maxSizeMb, ...hookArgs });

  return (
    <FileUploadPresentation
      type={type}
      value={internalValue}
      isDragging={isDragging}
      validationError={validationError}
      error={error}
      acceptAttr={accept.map(ext => `.${ext}`).join(',')}
      isUploading={isUploading}
      disabled={disabled}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onFileInputChange={handleFileInputChange}
      onRemove={handleRemove}
      fileInputRef={fileInputRef}
      defaultFiles={rest.isMultiple ? rest.defaultFiles : undefined}
      onDefaultFileRemove={onDefaultFileRemove}
      isMultiple={rest.isMultiple}
    />
  );
};
